import { ProductStock } from './stock.types';

export const PRODUCT_STOCK_COLLECTION = 'productStock';

/**
 * Réplica de solo lectura de `productStock` en la base `enterprise` (ver `ENTERPRISE_DATABASE_ID`),
 * mantenida por el trigger de `productStock`. La escriben SOLO el trigger y los CLIs de backfill y
 * reconcile: ningún flujo de negocio escribe acá.
 *
 * Es una copia **1:1**, sin filtros: cada doc del origen tiene su espejo, `stockQty: null` incluido.
 * Filtrar acá no ahorraría documentos (un doc filtrado deja tombstone igual, ver abajo) y le daría al
 * tombstone dos significados distintos. Quien lea decide qué le sirve — el endpoint `/stock` descarta
 * los `null` en memoria.
 *
 * **Los borrados dejan un tombstone, no un hueco**: un doc con solo `_sourceUpdateTime` y `_expiresAt`.
 * Sin él, borrar destruiría el testigo de orden y una entrega tardía del evento anterior recrearía
 * datos viejos para siempre. Como no tiene `establishmentId`, no matchea las queries del lector: no
 * hace falta filtrarlo ni indexarlo. Los limpia una política TTL sobre `_expiresAt`.
 */
export const PRODUCT_STOCK_REPLICA_COLLECTION = 'productStockReplica';

/** Datos de un write a `productStock`. `stockQty: null` = infinito/sin control (persiste el doc, conserva historial). */
export type ProductStockWriteData = Omit<
  ProductStock,
  'id' | 'updated' | 'stockQty' | 'lowStockThreshold'
> & {
  stockQty: number | null;
};

/**
 * Un write de `productStock` es SIEMPRE un `upsert` (valor absoluto finito o `null`). El **borrado**
 * del doc queda SOLO para remociones reales de entidad (quitar presentación/item, borrar producto,
 * huérfanos), que se ejecutan con `tx.delete`/`bulkWriter.delete` directo, no por esta vía.
 */
export type ProductStockWrite = { action: 'upsert'; data: ProductStockWriteData };

/** Una op de write de productStock resuelta: el `entityId` es el doc id (= id de entidad). */
export type ProductStockWriteOp = { entityId: string; write: ProductStockWrite };

/**
 * Resuelve el write de `productStock` para un cambio de stock: siempre `upsert` con el valor absoluto,
 * sea finito o `null`. Persistir `stockQty: null` (en vez de borrar el doc) al desactivar el control
 * conserva el historial de `productStockMovements` (baja de control ≠ pérdida de historial); el trigger
 * de kardex ve el `null` y registra un movimiento `control-disabled`. El caller ejecuta `tx.set`
 * con `merge: true` (conserva el mínimo de aviso `lowStockThreshold`) borrando los campos de contexto que
 * el write no trae y agregando `updated: serverTimestamp()`. El **doc id es el id de la
 * entidad** (`entityId`): producto/option-item = doc id de Firestore; presentación = id normalizado.
 *
 * Convención de lectura: **todos los reads de `productStock` van por `getProductStockConverter`**
 * (parsea `updated` → Date). Los writes van raw (serverTimestamp incompatible con el converter).
 */
export function resolveProductStockWrite(data: ProductStockWriteData): ProductStockWrite {
  return { action: 'upsert', data };
}
